import React, { useId } from 'react';
import { Box } from '@mui/material';

function BrandSvg({ size = 18, children, viewBox = '0 0 24 24', ...rest }) {
  return (
    <Box
      component="svg"
      viewBox={viewBox}
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        display: 'block',
        flexShrink: 0,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Google Gemini spark / four-point star mark */
export function GeminiIcon({ size = 18 }) {
  const gradId = useId().replace(/:/g, '');
  return (
    <BrandSvg size={size} viewBox="0 0 24 24">
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1C69FF" />
          <stop offset="50%" stopColor="#D96570" />
          <stop offset="100%" stopColor="#F0B11A" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M12 2c1.4 4.6 3.4 6.6 8 8-4.6 1.4-6.6 3.4-8 8-1.4-4.6-3.4-6.6-8-8 4.6-1.4 6.6-3.4 8-8z"
      />
    </BrandSvg>
  );
}

/** OpenAI / ChatGPT mark */
export function GptIcon({ size = 18 }) {
  return (
    <BrandSvg size={size} viewBox="0 0 24 24">
      <path
        fill="#10A37F"
        d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.39a5.985 5.985 0 0 0-3.998 2.898 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.368v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0L4.1 13.69A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.778-5.833-3.387L15.119 7.12a.076.076 0 0 1 .071 0l4.83 2.786a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.668zm2.01-3.023.143.085-4.782 2.782a.776.776 0 0 0-.785 0L9.68 9.15V6.818a.066.066 0 0 1 .028-.061L14.56 3.85a4.494 4.494 0 0 1 6.387 4.801zm-12.64 4.345-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L9.083 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"
      />
    </BrandSvg>
  );
}

/** Anthropic Claude mark */
export function ClaudeIcon({ size = 18 }) {
  return (
    <BrandSvg size={size} viewBox="0 0 24 24">
      <path
        fill="#D97757"
        d="M13.827 3.52h3.603L24 20.48h-3.603l-1.603-3.703H9.845l-1.604 3.703H4.637L13.827 3.52zm.664 4.155-2.927 6.76h5.854l-2.927-6.76zM5.26 3.52h3.604L2.4 20.48H0L5.26 3.52z"
      />
    </BrandSvg>
  );
}

/** Perplexity asterisk mark */
export function PerplexityIcon({ size = 18 }) {
  return (
    <BrandSvg size={size} viewBox="0 0 24 24">
      <path
        fill="#20808D"
        d="M19.785 0v7.272H22.5V17.99h-2.738v5.999h-3.543v-5.999h-2.94l6.003-7.271V0h3.503zm-6.182 7.272l-3.543 4.286v3.005h3.543V7.272zm-4.286 4.286l-3.543-4.286H2.232v7.291h3.543v4.714h3.542v-7.719zM5.775 0l6.003 7.272H8.235L2.232 0h3.543z"
      />
    </BrandSvg>
  );
}

const ICON_BY_PROVIDER = {
  gemini: GeminiIcon,
  gpt: GptIcon,
  claude: ClaudeIcon,
  perplexity: PerplexityIcon,
};

export function AskProviderIcon({ provider, size = 18 }) {
  const Icon = ICON_BY_PROVIDER[provider] || GeminiIcon;
  return <Icon size={size} />;
}

/** For MUI Button startIcon — accepts size or fontSize. */
export function AskProviderStartIcon({ provider, size = 14, fontSize }) {
  const resolvedSize = typeof size === 'number'
    ? size
    : (typeof fontSize === 'number' ? fontSize : 14);
  return <AskProviderIcon provider={provider} size={resolvedSize} />;
}
